<script setup lang="ts">
import type { VisibilityOption } from './usePrivacy';
import type { Translate } from '@/plugins/i18n/types';
import { computed, inject, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import ActionButton from '@/components/buttons/ActionButton.vue';
import AfModal from '@/components/overlays/AfModal.vue';
import { useModalStore } from '@/features/overlays/modals/modalStore';
import PrivacyCards from './PrivacyCards.vue';
import { usePrivacy } from './usePrivacy';
import { ALButton } from '@af-lib/vue-ui';

const { savePrivacy, visibilityDetails } = usePrivacy();
const modalStore = useModalStore();
const { smAndDown } = useDisplay();

const t = inject('t') as Translate;

const loadingSave = ref(false);
const visibilityValue = ref<VisibilityOption['value']>();

watch(
  visibilityDetails,
  (visibilityDetails) => {
    visibilityValue.value = visibilityDetails.value;
  },
  { immediate: true },
);

async function saveAndClose() {
  try {
    loadingSave.value = true;
    await savePrivacy(visibilityValue.value);
  } catch (error) {
    console.error('Error saving privacy settings:', error);
  } finally {
    loadingSave.value = false;
    modalStore.toggle('privacy');
  }
}

function close() {
  modalStore.toggle('privacy');
  visibilityValue.value = visibilityDetails.value.value;
}

const privacyUrl = t('general.privacy.url');
const privacyTextWithLink = t('privacy.learn_more.body_2');
const privacyTextWithUrl = privacyTextWithLink.replace(
  /<a>/,
  `<a href="${privacyUrl}" target="_blank" rel="noopener noreferrer" class="underline hover:no-underline">`,
);

const isDisabled = computed(() => {
  return visibilityValue.value === 2 || visibilityValue.value === visibilityDetails.value.value;
});
</script>

<template>
  <AfModal
    v-model="modalStore['privacy'].visible"
    max-width="1120px"
    :fullscreen="smAndDown"
    full-height
  >
    <template #title>
      <span>{{ $t('candidate_profile.share_profile.privacy.title') }}</span>
    </template>

    <template #content>
      <div class="px-6 grid gap-8">
        <div class="mt-5">
          <div class="text-body-3 text-center max-w-[100ch] mx-auto">
            {{ $t('candidate_profile.share_profile.privacy.description_1') }}
          </div>
        </div>

        <PrivacyCards v-model="visibilityValue" context="settings"></PrivacyCards>

        <p
          class="text-body-3 text-center mb-5 max-w-[100ch] mx-auto"
          v-html="$t('privacy.learn_more.body_1') + privacyTextWithUrl"
        ></p>
      </div>
    </template>

    <template #footer>
      <ActionButton cancel @click="close" class="me-5">{{ $t('general.cancel') }}</ActionButton>
      <ALButton :isLoading="loadingSave" @click="saveAndClose" :isDisabled="isDisabled">
        {{ $t('general.save') }}
      </ALButton>
    </template>
  </AfModal>
</template>
